import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TablePagination,
  Chip,
} from "@mui/material";
import api from "../../utils/api";

/* ==============================
   Helpers
============================== */
function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatWeek(weekStart) {
  if (!weekStart) return "—";
  try {
    const d = new Date(weekStart);
    const end = new Date(d);
    end.setDate(d.getDate() + 6);
    return `${d.toDateString()} – ${end.toDateString()}`;
  } catch {
    return weekStart;
  }
}

export default function AdminJambHistoryPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  // Server-side pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const offset = useMemo(
    () => page * rowsPerPage,
    [page, rowsPerPage]
  );

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await api.get("/api/admin/jamb/history", {
        params: {
          limit: rowsPerPage,
          offset,
        },
      });

      setRows(res.data?.history || []);
      setTotal(Number(res.data?.total || 0));
    } catch (e) {
      setError("Failed to load admin JAMB history.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, offset]);

  /* ==============================
     Loading / Error
============================== */
  if (loading) {
    return (
      <Box mt={6} display="flex" justifyContent="center">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box maxWidth={1100} mx="auto" mt={4}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box maxWidth={1300} mx="auto" p={2}>
      {/* ================= Header ================= */}
      <Box mb={3}>
        <Typography variant="h5" fontWeight={800}>
          🧑‍💼 Admin — JAMB Attempt History
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Monitor all students’ JAMB participation and completion status.
        </Typography>
      </Box>

      {/* ================= Table ================= */}
      <Paper sx={{ borderRadius: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Week</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Completed At</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary">
                      No JAMB attempts found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r, index) => (
                  <TableRow key={r.attempt_id}>
                    <TableCell>
                      {page * rowsPerPage + index + 1}
                    </TableCell>

                    <TableCell>{r.student_name}</TableCell>
                    <TableCell>{r.email}</TableCell>

                    <TableCell>{formatWeek(r.week_start)}</TableCell>

                    <TableCell>
                      {r.total_score != null
                        ? `${r.total_score} / 400`
                        : "—"}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={r.status}
                        color={
                          r.status === "completed"
                            ? "success"
                            : "warning"
                        }
                        size="small"
                      />
                    </TableCell>

                    <TableCell>{formatDate(r.completed_at)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ================= Pagination ================= */}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Paper>
    </Box>
  );
}
